import { z } from 'zod';
import { getAuth } from 'firebase/auth';
import { isIOS } from '@/domains/shared/utils/native';

const BASEURL = `${process.env.NEXT_PUBLIC_API_URL}`;

const RegisterIterableDeviceTokenInputSchema = z.object({
  token: z.string(),
});

const RawIterablePushMetadataSchema = z
  .object({
    campaignId: z.number().optional(),
    templateId: z.number(),
    messageId: z.string(),
  })
  .passthrough();

const TrackIterablePushOpenInputSchema = z.object({
  campaignId: z.number().optional(),
  templateId: z.number(),
  messageId: z.string(),
});

type RegisterIterableDeviceTokenInput = z.infer<
  typeof RegisterIterableDeviceTokenInputSchema
>;

type TrackIterablePushOpenInput = z.infer<
  typeof TrackIterablePushOpenInputSchema
>;

const environment = process.env.NEXT_PUBLIC_PLATFORM_APP_ENV;

const isIosPlatform = isIOS();

const platform = isIosPlatform
  ? environment === 'production' || environment === 'staging'
    ? 'APNS'
    : 'APNS_SANDBOX'
  : 'GCM';

export const registerIterableDeviceToken = async (
  input: RegisterIterableDeviceTokenInput,
) => {
  RegisterIterableDeviceTokenInputSchema.parse(input);
  const { token } = input;

  // Normalize iOS (APNS) token: remove hyphens + lowercase
  const normalizedToken = normalizePushToken(token);
  await postWithAuth('/api/iterable/push/registerDeviceToken', {
    token: normalizedToken,
    platform,
  });
};

export const trackIterablePushOpen = async (
  input: TrackIterablePushOpenInput,
) => {
  TrackIterablePushOpenInputSchema.parse(input);
  await postWithAuth('/api/iterable/push/trackPushOpen', input);
};

export const disableIterableDevice = async () => {
  const token = localStorage.getItem('fcm_token');
  if (!token) return;

  // Normalize iOS (APNS) token: remove hyphens + lowercase
  await postWithAuth('/api/iterable/push/disableDevice', {
    token: normalizePushToken(token),
    platform,
  });
};

const normalizePushToken = (token: string): string => {
  return isIosPlatform ? token.replace(/-/g, '').toLowerCase() : token;
};

const postWithAuth = async (
  path: string,
  body: Record<string, string | number>,
): Promise<void> => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.warn(`[${path}] Skipping request — no authenticated user.`);
      return;
    }

    console.log('DATA FOR POST REQUEST:', {
      PATH: path,
      BODY: body,
      BASE_URL: BASEURL,
    });

    const request = async (token: string) =>
      fetch(`${BASEURL}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'client-id': process.env.NEXT_PUBLIC_CLIENT_ID || '',
        },
        body: JSON.stringify(body),
      });

    let token = await currentUser.getIdToken();
    let res = await request(token);

    if (res.status === 401) {
      token = await currentUser.getIdToken(true);
      res = await request(token);
    }

    if (!res.ok) {
      console.error(`[${path}] request failed:`, await res.text());
    }
  } catch (err) {
    console.error(`[${path}] unexpected error:`, err);
  }
};

/**
 * Pull the deep-link URL out of an Iterable push payload.
 *
 * Iterable stores the tap action inside the `itbl` object as `defaultAction`
 * (`{ type: 'openUrl', data: '<url>' }`). We also accept a plain `url` field on
 * the payload as a fallback for custom setups. `notification.data` values can
 * arrive already-parsed (iOS) or as JSON strings (Android), so we handle both.
 */
export const extractIterableDeepLinkUrl = (
  notificationData: unknown,
): string | null => {
  try {
    if (!notificationData) return null;

    const data =
      typeof notificationData === 'string'
        ? JSON.parse(notificationData)
        : notificationData;
    if (!data || typeof data !== 'object') return null;

    const record = data as Record<string, unknown>;

    const rawItbl = record.itbl;
    const itbl = typeof rawItbl === 'string' ? JSON.parse(rawItbl) : rawItbl;
    const action = (itbl as Record<string, unknown> | undefined)
      ?.defaultAction as { type?: string; data?: unknown } | undefined;

    if (
      action &&
      typeof action.data === 'string' &&
      (action.type === 'openUrl' ||
        action.type === 'deepLink' ||
        action.type === undefined)
    ) {
      return action.data;
    }

    if (typeof record.url === 'string') return record.url;

    return null;
  } catch (err) {
    console.warn('Failed to parse Iterable deep link:', notificationData, err);
    return null;
  }
};

export const extractIterablePushMetadata = (
  data: unknown,
): TrackIterablePushOpenInput | null => {
  try {
    if (!data) return null;
    // 'data' may arrive as a JSON string (on Android) or an already-parsed object (on iOS).
    const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
    const { campaignId, templateId, messageId } =
      RawIterablePushMetadataSchema.parse(parsedData);
    if (!campaignId) return null;
    return { campaignId, templateId, messageId };
  } catch (err) {
    console.warn('Failed to parse Iterable metadata:', data, err);
    return null;
  }
};
